<script setup lang="ts">
import { Drawer, Button, ProgressBar } from 'primevue';
import { useSelectedLogSorterStore, type Bin } from '../stores/selectedLogSorterStore';
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import LogSorterPanel from './LogSorterPanel.vue';
import { useUpdateLogSorter, type LogSorter, type UpdateLogSorterRequest } from '@/logSorter/api/updateLogSorter';
import { Assert } from 'shared/assert';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { useCreateLogSorter, type CreateLogSorterRequest } from '@/logSorter/api/createLogSorter';
import type { LogSorterBin } from '@/logSorter/api/common/logSorterBin';
import { useUpdateShape, type UpdateShapeRequest } from '@/shapes/api/updateShape';
import { useCreateShape, type CreateShapeRequest } from '@/shapes/api/createShape';
import { useCreateStorageArea, type CreateStorageAreaRequest } from '@/storageAreas/api/createStorageArea';
import { StorageAreaStatus } from 'shared/storageAreas/domain/storageAreaStatus';
import { StorageAreaType } from 'shared/storageAreas/domain/storageAreaType';
import { useSettingStore } from 'shared/setting/stores/settingStore';
import { computeStorageAreaDisplayText } from 'shared/storageAreas/domain/computeDisplayText';
import { createStorageArea as buildStorageArea } from 'shared/storageAreas/domain/storageArea';
import { useQueryShape } from '@/shapes/api/queryShape';
import { useDeleteStorageArea } from '@/storageAreas/api/deleteStorageArea';
import { useDeleteLogSorterConfirm } from '@/logSorter/composables/deleteLogSorterConfirm';
import LogSorterBinsPanel from './LogSorterBinsPanel.vue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';

const store = useSelectedLogSorterStore();
const active = ref<boolean>(false);
const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const actionFailedAlert = useActionFailedAlert();

const createLogSorter = useCreateLogSorter();
const updateLogSorter = useUpdateLogSorter();

const createShape = useCreateShape();
const updateShape = useUpdateShape();
const queryShape = useQueryShape();

const createStorageArea = useCreateStorageArea();
const deleteStorageArea = useDeleteStorageArea();
const settingStore = useSettingStore();

const loading = ref<boolean>(false);

/* Progress tracking */
const totalRequests = ref<number>(0);
const completedRequests = ref<number>(0);
const progressPercent = computed(() => {
  if (totalRequests.value === 0) return 0;
  return Math.round((completedRequests.value / totalRequests.value) * 100);
});

const activeTab = ref<'logSorter' | 'bins'>('logSorter');

const {
  severity: logSorterTabSeverity,
  variant: logSorterTabVariant
} = tabState('logSorter');

const {
  severity: binsTabSeverity,
  variant: binsTabVariant
} = tabState('bins');

const deleteLogSorterConfirm = useDeleteLogSorterConfirm();

const {
  name,
  operation,
  changed,
  invalid
} = storeToRefs(store);

const saveDisabled = computed(() => {

  if (loading.value) return true;
  if (invalid.value) return true;

  switch (operation.value) {
    case 'create':
      return false;
    case 'edit':
      return !changed.value;
    default:
      return true;
  }
});

const deleteDisabled = computed(() => {
  switch (operation.value) {
    case 'edit':
      return false;
  }

  return true;
});


watch(operation, (newOperation) => {
  active.value = newOperation !== 'none';
});

const title = computed(() => {
  if (name.value === undefined && operation.value === 'create') {
    return translations.value.logSorter.drawer.createTitle;
  }
  return name.value;
});

async function onSaveClick() {

  if (operation.value === 'edit') {
    await update();
  } else if (operation.value === 'create') {
    await create();
  }
}

async function create() {
  try {
    loading.value = true;

    // total requests: for each bin -> createStorageArea + createShape = 2 per bin, +1 for createLogSorter
    const binsArr = store.bins;
    totalRequests.value = binsArr.length * 2 + 1;
    completedRequests.value = 0;

    const {
      name,
      addLogWhenSorted,
      configuration,
      bins,
      intakes
    } = store;

    Assert.definied(name);

    const logSorterBins: LogSorterBin[] = [];

    for (const bin of bins) {
      const createdBin = await createBin(bin);
      if (createdBin === undefined) return;
      logSorterBins.push(createdBin);
    }

    const request: CreateLogSorterRequest = {
      logSorter: {
        name: name,
        addLogWhenSorted: addLogWhenSorted,
        bins: logSorterBins,
        intakes: intakes,
        configuration: JSON.stringify(configuration),
      }
    };

    const response = await createLogSorter.execute(request);
    completedRequests.value++;

    if (!response.ok) {
      actionFailedAlert.show(response.details);
      return;
    }

    store.edit({
      logSorter: response.logSorter
    });
  } finally {
    loading.value = false;
    // leave progress visible briefly? we keep progress values; UI hides when loading false per template v-if
  }
}

async function update() {
  try {
    loading.value = true;

    // estimate total requests:
    // per bin we may do queryShape + updateShape (worst-case 2), bins without storageAreaId -> createStorageArea + createShape = 2
    // so count 2 per bin + deleteStorageAreaIds + 1 for updateLogSorter
    const binsArr = store.bins;
    const deleteIds = store.deleteStorageAreaIds ?? new Set<string>();
    totalRequests.value = binsArr.length * 2 + (deleteIds.size ?? 0) + 1;
    completedRequests.value = 0;

    const {
      logSorterId,
      name,
      addLogWhenSorted,
      configuration,
      bins,
      deleteStorageAreaIds,
      intakes
    } = store;

    Assert.definied(logSorterId);
    Assert.definied(name);

    const logSorterBins: LogSorterBin[] = [];

    for (const bin of bins) {
      const storageAreaId = bin.storageAreaId;

      if (storageAreaId) {

        const shapeResponse = await queryShape.execute({
          storageAreaId: storageAreaId
        });
        completedRequests.value++;

        if (!shapeResponse.ok) {
          actionFailedAlert.show(shapeResponse.details);
          return;
        }

        if (shapeResponse.shapes.length > 0) {
          const shape = shapeResponse.shapes[0];
          if (shape) {
            const shapeId = shape.id;
            const shapeVersion = shape.version;
            const updatedBin = await updateBin({
              bin,
              shapeId,
              storageAreaId,
              shapeVersion
            });
            if (updatedBin === undefined) return;
            logSorterBins.push(updatedBin);
          }
        } else {
          // no shape found - create new storage area/shape for this bin (rare)
          const createdBin = await createBin(bin);
          if (createdBin === undefined) return;
          logSorterBins.push(createdBin);
        }

      } else {
        const createdBin = await createBin(bin);
        if (createdBin === undefined) return;
        logSorterBins.push(createdBin);
      }
    }

    for (const storageAreaId of deleteStorageAreaIds) {
      const deleteResponse = await deleteStorageArea.execute({ storageAreaId });
      completedRequests.value++;
      if (!deleteResponse.ok) {
        actionFailedAlert.show(deleteResponse.details);
        return;
      }
    }

    const logSorter: LogSorter = {
      name: name,
      addLogWhenSorted: addLogWhenSorted,
      bins: logSorterBins,
      intakes: intakes,
      configuration: JSON.stringify(configuration),
    };

    const request: UpdateLogSorterRequest = {
      id: logSorterId,
      logSorter: logSorter,
    };

    const response = await updateLogSorter.execute(request);
    completedRequests.value++;

    if (!response.ok) {
      actionFailedAlert.show(response.details);
      return;
    }

    store.edit({
      logSorter: response.logSorter
    });
  } finally {
    loading.value = false;
  }
}

async function createBin(bin: Bin): Promise<LogSorterBin | undefined> {
  const storageAreaRequest: CreateStorageAreaRequest = {
    storageArea: {
      active: true,
      name: bin.name,
      temporary: false,
      status: StorageAreaStatus.Empty,
      type: StorageAreaType.LogSorterBin,
      version: crypto.randomUUID(),
      allowedProducts: [],
      logYardData: {
        allowMultipleProducts: false,
        disableAutomaticProductChange: false,
        sideArea: 0
      },
      coilStack: null,
      bulkStorageData: null,
      note: null,
      importantNote: false,
      displayText: computeStorageAreaDisplayText(
        settingStore.setting.sharedClient.storageArea.displayTextFormula,
        { ...buildStorageArea(crypto.randomUUID()), name: bin.name, type: StorageAreaType.LogSorterBin, status: StorageAreaStatus.Empty },
      ),
    }
  };

  const storageAreaResponse = await createStorageArea.execute(storageAreaRequest);
  completedRequests.value++;

  if (!storageAreaResponse.ok) {
    actionFailedAlert.show(storageAreaResponse.details);
    return;
  }

  const shapeRequest: CreateShapeRequest = {
    shape: {
      shapeGeoJson: bin.geoJson.shape,
      geofenceGeoJson: bin.geoJson.geofence,
      storageAreaId: storageAreaResponse.storageArea.id,
      version: crypto.randomUUID(),
      color: null,
      zIndex: 0,
    }
  };

  const shapeResponse = await createShape.execute(shapeRequest);
  completedRequests.value++;

  if (!shapeResponse.ok) {
    actionFailedAlert.show(shapeResponse.details);
    return;
  }

  return {
    binIdentifier: bin.identifier,
    storageAreaId: storageAreaResponse.storageArea.id,
    binNumber: bin.number,
    width: bin.width,
    widthSpecified: bin.widthSpecified,
    length: bin.length,
    lengthSpecified: bin.lengthSpecified,
    offsetY: bin.offsetY,
    offsetX: bin.offsetX
  };
}

async function updateBin(action: UpdateBin): Promise<LogSorterBin | undefined> {

  const {
    bin,
    shapeId,
    storageAreaId,
    shapeVersion
  } = action;

  const shapeRequest: UpdateShapeRequest = {
    shapeId: shapeId,
    shape: {
      shapeGeoJson: bin.geoJson.shape,
      geofenceGeoJson: bin.geoJson.geofence,
      storageAreaId: storageAreaId,
      version: shapeVersion,
      color: null,
      zIndex: 0
    }
  };

  const shapeResponse = await updateShape.execute(shapeRequest);
  completedRequests.value++;

  if (!shapeResponse.ok) {
    actionFailedAlert.show(shapeResponse.details);
    return;
  }

  return {
    binIdentifier: bin.identifier,
    storageAreaId: storageAreaId,
    binNumber: bin.number,
    width: bin.width,
    widthSpecified: bin.widthSpecified,
    length: bin.length,
    lengthSpecified: bin.lengthSpecified,
    offsetY: bin.offsetY,
    offsetX: bin.offsetX,
  };
}

async function onDeleteClick() {
  const {
    logSorterId,
  } = store;

  Assert.definied(logSorterId);

  if (await deleteLogSorterConfirm.show({ logSorterId })) {
    store.hide();
  }
}

function tabState(value: string) {
  return {
    severity: tabSeverity(value),
    variant: tabVariant(value)
  };
}

function tabSeverity(value: string) {
  return computed(() => activeTab.value === value ? 'primary' : undefined);
}

function tabVariant(value: string) {
  return computed(() => activeTab.value !== value ? 'outlined' : undefined);
}

interface UpdateBin {
  shapeId: string;
  storageAreaId: string;
  bin: Bin;
  shapeVersion: string;
}

function closePanel() {
  store.hide();
}

</script>

<template>
  <div v-if="active" class="logsorter-drawer">
    <div class="drawer-close">
      <Button icon="pi pi-times" rounded text severity="secondary" aria-label="Close" @click="closePanel" />
    </div>

    <div class="drawer-title">
      {{ name }}
    </div>

    <div class="tabs">
      <Button :severity="logSorterTabSeverity" :variant="logSorterTabVariant" @click="activeTab = 'logSorter'">{{
        translations.logSorter.drawer.logSorterTab }}</Button>
      <Button :severity="binsTabSeverity" :variant="binsTabVariant" @click="activeTab = 'bins'">{{
        translations.logSorter.drawer.binsTab }}</Button>
    </div>

    <div class="body">
      <LogSorterPanel v-if="activeTab === 'logSorter'"></LogSorterPanel>
      <LogSorterBinsPanel v-if="activeTab === 'bins'"></LogSorterBinsPanel>
    </div>

    <!-- Progress bar visible during saving -->
    <div v-if="loading" style="margin-top: 1rem;">
      <ProgressBar :value="progressPercent" />
    </div>

    <div class="actions">

      <Button :disabled="saveDisabled" :loading="loading" class="save-button" :label="translations.generic.save"
        icon="pi pi-save" @click="onSaveClick"></Button>

      <Button :disabled="deleteDisabled" class="delete-button" severity="danger" :label="translations.generic.delete"
        icon="pi pi-trash" @click="onDeleteClick"></Button>

    </div>
  </div>
</template>

<style scoped>
.logsorter-drawer {
  position: relative;
  height: 100% !important;
  width: 100% !important;
  padding: 1em;
  display: flex;
  flex-direction: column;
}

.drawer-close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 10;
}

.drawer-title {
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 1.4;

  padding-bottom: 0.75rem;
  margin-bottom: 1rem;

  border-bottom: 1px solid var(--surface-border);
  color: var(--text-color);
}

.tabs {
  display: flex;
  column-gap: .5rem;
}

.body {
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: .5rem;
}

.actions {
  margin-top: auto;
  padding-top: 1rem;
  display: flex;
  flex-direction: row;
  gap: 1rem;
  justify-content: flex-start;
}
</style>
